import { AnimatePresence, motion } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import Bell from '@/assets/svgs/bell.svg?react';
import Blog from '@/assets/svgs/blog.svg?react';
import Calendar from '@/assets/svgs/calendar.svg?react';
import Chat from '@/assets/svgs/chat.svg?react';
import ChevronDown from '@/assets/svgs/chevron-down.svg?react';
import ChevronUp from '@/assets/svgs/chevron-up.svg?react';
import Coffee from '@/assets/svgs/coffee.svg?react';
import Cog from '@/assets/svgs/cog.svg?react';
import DocumentText from '@/assets/svgs/document-text.svg?react';
import Event from '@/assets/svgs/event.svg?react';
import Invoice from '@/assets/svgs/invoice.svg?react';
import ListBullet from '@/assets/svgs/list-bullet.svg?react';
import Logout from '@/assets/svgs/logout.svg?react';
import Mail from '@/assets/svgs/mail.svg?react';
import SpeakerPhone from '@/assets/svgs/speaker-phone.svg?react';
import Users from '@/assets/svgs/users.svg?react';
import ViewGrid from '@/assets/svgs/view-grid.svg?react';
import Warranty from '@/assets/svgs/warranty.svg?react';
import type { MenuItem } from '@/components/layout/navigation-menu';
import { Text } from '@/components/Text';

const ICON_MAP: Record<string, React.ReactNode> = {
  dashboard: <ViewGrid className='size-6! text-white' />,
  'lead-group': <SpeakerPhone className='size-6! text-white' />,
  'fmi-leads': <SpeakerPhone className='size-6! text-white' />,
  'fmi-enquiries': <Mail className='size-6! text-white' />,
  leads: <Mail className='size-6! text-white' />,
  warranty: <Warranty className='size-6! text-white' />,
  claims: <DocumentText className='size-6! text-white' />,
  'historical-claims': <DocumentText className='size-6! text-white' />,
  jobs: <DocumentText className='size-6! text-white' />,
  'glazier-invoices': <Invoice className='size-6! text-white' />,
  marketing: <SpeakerPhone className='size-6! text-white' />,
  events: <Event className='size-6! text-white' />,
  blogs: <Blog className='size-6! text-white' />,
  'app-notifications': <Bell className='size-6! text-white' />,
  'admin-panel': <Cog className='size-6! text-white' />,
  roles: <Warranty className='size-6! text-white' />,
  customers: <Users className='size-6! text-white' />,
  fabricators: <Users className='size-6! text-white' />,
  glaziers: <Users className='size-6! text-white' />,
  'fmi-users': <Users className='size-6! text-white' />,
  suppliers: <Users className='size-6! text-white' />,
  'fabricator-members': <Users className='size-6! text-white' />,
  messages: <Chat className='size-6! text-white' />,
  settings: <Cog className='size-6! text-white' />,
  'sign-out': <Logout className='size-6! text-white' />,
  'coffee-console': <Coffee className='size-6! text-white' />,
  'sales-hub': <Chat className='size-6! text-white' />,
  calendar: <Calendar className='size-6! text-white' />,
  meetings: <ViewGrid className='size-6! text-white' />,
  'action-items': <ListBullet className='size-6! text-white' />,
  'fyi-items': <DocumentText className='size-6! text-white' />,
};

interface Props {
  item: MenuItem;
  closeMenu: () => void;
}

export const NavigationMenuItem = ({ item, closeMenu }: Props) => {
  const { pathname } = useLocation();

  // Component states
  const [isExpanded, setIsExpanded] = useState(!!item.items?.length);

  // Component defaults
  const isExpandable = !!item.items?.length;
  const Icon = useMemo(() => {
    return ICON_MAP[item.name] ?? null;
  }, [item.name]);
  const isActive = useMemo(() => {
    // Group rows (no path, children only) toggle their section — they are
    // never the active page, and an empty path would otherwise `startsWith`
    // every route.
    if (!item.path) return false;
    if (item.path === '/') return pathname === '/';
    return pathname.startsWith(item.path);
  }, [pathname, item.path]);
  const activeClass = isActive ? 'bg-white_20' : '';
  const commonClass = `${activeClass} w-full px-2 py-2.5 rounded-lg flex items-center justify-between select-none cursor-pointer`;

  const content = (
    <>
      <div className='flex items-center gap-3'>
        {!!Icon && Icon}
        <Text renderAs='pmedium' className='text-white'>
          {item.label}
        </Text>
      </div>

      {isExpandable ? (
        isExpanded ? (
          <ChevronUp className='size-5! text-white' />
        ) : (
          <ChevronDown className='size-5! text-white' />
        )
      ) : null}
    </>
  );

  return (
    <>
      {isExpandable ? (
        <div className={commonClass} onClick={() => setIsExpanded(!isExpanded)}>
          {content}
        </div>
      ) : (
        <Link className={commonClass} to={item.path} onClick={closeMenu}>
          {content}
        </Link>
      )}

      {isExpandable && (
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial='collapsed'
              animate='open'
              exit='collapsed'
              variants={{
                open: {
                  height: (item.items || []).length * 44,
                },
                collapsed: { height: 0 },
              }}
              transition={{
                duration: 0.3,
                ease: [0.04, 0.62, 0.23, 0.98],
              }}
              className='ml-4 overflow-hidden'
            >
              {(item.items || []).map((item) => (
                <NavigationMenuItem
                  key={item.label}
                  item={item}
                  closeMenu={closeMenu}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </>
  );
};
